import { RouteRecordRaw } from "vue-router";
import { checkProductionSubscription } from "@/utils/navigationGuards";

export const productionRoutes: RouteRecordRaw[] = [
	{
		path: "/productions/:productionId",
		name: "production-overview",
		props: true,
		component: () => import("@/views/productions/ProductionOverview.vue"),
		beforeEnter: checkProductionSubscription,
		meta: {
			pageName: "Production Overview",
			//permission: RoutePermission.Access,
			permissionId: "productionId",
			friendlyPath: "/productions/**",
		},
	},
	{
		path: "/productions/:productionId/onboarding",
		name: "production-onboarding",
		props: true,
		component: () => import("@/components/Onboarding/ProductionOnboarding.vue"),
		beforeEnter: checkProductionSubscription,
		meta: {
			pageName: "Production Onboarding",
			//permission: RoutePermission.Access,
			permissionId: "productionId",
			friendlyPath: "/productions/**/onboard",
		},
	},
	{
		path: "/productions/:productionId/activation/select-plan",
		name: "production-activation-select-plan",
		props: true,
		component: () => import("@/views/productions/activation/ProductionActivationStartView.vue"),
		meta: {
			pageName: "Production Select Plan",
			//permission: RoutePermission.Access,
			permissionId: "productionId",
			friendlyPath: "/productions/**/activation/select-plan",
		},
	},
	{
		path: "/productions/:productionId/activation/start",
		name: "production-activation-start",
		props: true,
		component: () => import("@/views/productions/activation/ProductionNotActivatedView.vue"),
		meta: {
			pageName: "Production Not Activated",
			//permission: RoutePermission.Access,
			permissionId: "productionId",
			friendlyPath: "/productions/**/activation/start",
		},
	},
	{
		path: "/productions/:productionId/activation/notice",
		name: "production-subscription-notice",
		props: true,
		component: () => import("@/views/productions/activation/ProductionSubscriptionIssueView.vue"),
		meta: {
			pageName: "Production Subscription Issue",
			//permission: RoutePermission.Access,
			permissionId: "productionId",
			friendlyPath: "/productions/**/activation/notice",
		},
	},
	{
		path: "/productions/:productionId/activation/callback",
		name: "production-activation-callback",
		props: (route) => ({
			...route.params,
			checkoutSessionId: route.query.session_id,
		}),
		component: () => import("@/views/productions/activation/ProductionActivationCallbackView.vue"),
		meta: {
			pageName: "Production Activation Callback",
			//permission: RoutePermission.Access,
			permissionId: "productionId",
			friendlyPath: "/productions/**/activation/callback",
		},
	},
	{
		path: "/productions/:productionId/schedule",
		name: "production-schedule",
		props: true,
		beforeEnter: checkProductionSubscription,
		component: () => import("@/components/Entity/SchedulingTab.vue"),
		meta: {
			pageName: "Production Schedule",
			//permission: RoutePermission.Access,
			permissionId: "productionId",
			friendlyPath: "/productions/**/schedule",
			fullWidth: true,
			showDetailsHeader: true,
			header: "Production Schedule",
			showBackButton: false,
			actionButton: "Schedule Call",
			actionButtonEmits: "modal:scheduling:add",
			actionButtonManagerOnly: true,
		},
	},
	{
		path: "/productions/:productionId/communications",
		name: "production-communications",
		props: true,
		beforeEnter: checkProductionSubscription,
		component: () => import("@/components/Entity/CommunicationsTab.vue"),
		meta: {
			pageName: "Production Communications",
			//permission: RoutePermission.Access,
			permissionId: "productionId",
			friendlyPath: "/productions/**/communications",
			boxed: true,
			fullWidth: true,
			showDetailsHeader: true,
			header: "Communications",
			showBackButton: false,
			actionButton: "Send Communication",
			actionButtonEmits: "modal:communications:add",
			actionButtonManagerOnly: true,
		},
	},
	{
		path: "/productions/:productionId/reports",
		name: "production-reports",
		props: true,
		beforeEnter: checkProductionSubscription,
		component: () => import("@/components/Entity/ReportsTab.vue"),
		meta: {
			pageName: "Production Reports",
			//permission: RoutePermission.Access,
			permissionId: "productionId",
			friendlyPath: "/productions/**/reports",
			boxed: true,
			fullWidth: true,
		},
	},
	{
		path: "/productions/:productionId/reports/:reportId",
		name: "production-report",
		props: true,
		beforeEnter: checkProductionSubscription,
		component: () => import("@/components/Entity/Reports/ReportTab.vue"),
		meta: {
			pageName: "Production Report Home",
			//permission: RoutePermission.Access,
			permissionId: "productionId",
			friendlyPath: "/productions/**/reports/**",
			boxed: true,
			fullWidth: true,
			hideBottomNav: false,
			showDetailsHeader: true,
			header: "View Report",
		},
		children: [
			{
				path: "",
				name: "production-reports--viewer",
				props: true,
				component: () => import("@/components/Entity/Reports/ReportViewer.vue"),
				meta: {
					pageName: "Production Report Viewer",
					//permission: RoutePermission.Access,
					permissionId: "productionId",
					friendlyPath: "/productions/**/reports/**",
					boxed: true,
					fullWidth: true,
					hideBottomNav: true,
					showDetailsHeader: true,
					header: "View Report",
				},
			},
			{
				path: "comments",
				name: "production-reports--comments",
				props: true,
				component: () => import("@/components/Entity/Reports/ReportComments.vue"),
				meta: {
					pageName: "Production Report Comments",
					//permission: RoutePermission.Access,
					permissionId: "productionId",
					friendlyPath: "/productions/**/reports/**",
					boxed: true,
					fullWidth: true,
					hideBottomNav: true,
					showDetailsHeader: true,
					header: "View Report Comments",
				},
			},
			{
				path: "insights",
				name: "production-reports--insights",
				props: true,
				component: () => import("@/components/Entity/Reports/ReportInsights.vue"),
				meta: {
					pageName: "Production Report Insights",
					//permission: RoutePermission.Access,
					permissionId: "productionId",
					friendlyPath: "/productions/**/reports/**",
					boxed: true,
					fullWidth: true,
					hideBottomNav: true,
					showDetailsHeader: true,
					header: "View Report Insights",
				},
			},
			{
				path: "edit",
				name: "production-reports--editor",
				props: true,
				component: () => import("@/components/Entity/Reports/ReportEditor.vue"),
				meta: {
					pageName: "Production Report Editor",
					//permission: RoutePermission.Access,
					permissionId: "productionId",
					friendlyPath: "/productions/**/reports/**/edit",
					boxed: true,
					fullWidth: true,
					hideBottomNav: true,
					showDetailsHeader: true,
					header: "Edit Report",
					actionButton: "Save",
					actionButtonEmits: "reports:editor:update",
					actionButtonManagerOnly: true,
				},
			},
		],
	},
	{
		path: "/productions/:productionId/drive/:folderId?",
		name: "production-drive",
		props: true,
		beforeEnter: checkProductionSubscription,
		component: () => import("@/components/Entity/DriveTab.vue"),
		meta: {
			pageName: "Production Drive",
			//permission: RoutePermission.Access,
			permissionId: "productionId",
			friendlyPath: "/productions/**/drive",
		},
	},
	{
		path: "/productions/:productionId/availability",
		name: "production-availability-calendar",
		props: true,
		beforeEnter: checkProductionSubscription,
		component: () => import("@/components/Entity/AvailabilityTab.vue"),
		meta: {
			pageName: "Production Availability",
			//permission: RoutePermission.Manage,
			permissionId: "productionId",
			friendlyPath: "/productions/**/availability",
		},
	},
	{
		path: "/productions/:productionId/availability/calls",
		name: "production-availability-calls",
		props: true,
		beforeEnter: checkProductionSubscription,
		component: () => import("@/components/Calendar/CallsTable.vue"),
		meta: {
			pageName: "Production Calls",
			//permission: RoutePermission.Manage,
			permissionId: "productionId",
			friendlyPath: "/productions/**/availability/calls",
		},
	},
	{
		path: "/productions/:productionId/availability/requests",
		name: "production-availability-requests",
		props: true,
		beforeEnter: checkProductionSubscription,
		component: () => import("@/components/Entity/Scheduling/ProductionConflicts.vue"),
		meta: {
			pageName: "Production Availability Requests",
			//permission: RoutePermission.Manage,
			permissionId: "productionId",
			friendlyPath: "/productions/**/availability/requests",
		},
	},
	{
		path: "/productions/:productionId/settings",
		name: "production-settings",
		props: true,
		component: () => import("@/components/Entity/SettingsTab.vue"),
		meta: {
			pageName: "Production Settings",
			//permission: RoutePermission.Manage,
			permissionId: "productionId",
			friendlyPath: "/productions/**/settings",
		},
		children: [
			{
				path: "",
				name: "production-settings-home",
				props: true,
				component: () => import("@/components/Entity/EntitySettingsMenuMobile.vue"),
				meta: {
					pageName: "Production Settings",
					//permission: RoutePermission.Manage,
					permissionId: "productionId",
					friendlyPath: "/productions/**/settings/general",
				},
			},
			{
				path: "general",
				name: "production-settings-general",
				props: true,
				component: () => import("@/components/Entity/Settings/GeneralSettings.vue"),
				meta: {
					pageName: "Production Settings General",
					//permission: RoutePermission.Manage,
					permissionId: "productionId",
					friendlyPath: "/productions/**/settings/general",
				},
			},
			{
				path: "logo",
				name: "production-settings-logo",
				props: true,
				component: () => import("@/components/Entity/Settings/LogoSettings.vue"),
				meta: {
					pageName: "Production Settings Logo",
					//permission: RoutePermission.Manage,
					permissionId: "productionId",
					friendlyPath: "/productions/**/settings/logo",
				},
			},
			{
				path: "venues",
				name: "production-settings-venues",
				props: true,
				component: () => import("@/components/SelfContained/VenueSettings.vue"),
				beforeEnter: checkProductionSubscription,
				meta: {
					pageName: "Production Settings Venues",
					//permission: RoutePermission.Manage,
					permissionId: "productionId",
					friendlyPath: "/productions/**/settings/venues",
				},
			},
			{
				path: "call-types",
				name: "production-settings-call-types",
				props: true,
				component: () => import("@/components/SelfContained/CallTypeSettings.vue"),
				beforeEnter: checkProductionSubscription,
				meta: {
					pageName: "Production Settings Call Types",
					//permission: RoutePermission.Manage,
					permissionId: "productionId",
					friendlyPath: "/productions/**/settings/call-types",
				},
			},
			{
				path: "conflict-date-sets",
				name: "production-settings-conflict-date-sets",
				props: true,
				component: () => import("@/components/Entity/Settings/ConflictDateSetSettings.vue"),
				beforeEnter: checkProductionSubscription,
				meta: {
					pageName: "Production Settings Conflict Date Sets",
					//permission: RoutePermission.Manage,
					permissionId: "productionId",
					friendlyPath: "/productions/**/settings/conflict-date-sets",
					boxed: true,
					fullWidth: true,
				},
			},
			{
				path: "report-templates",
				name: "production-settings-report-templates",
				props: true,
				component: () => import("@/components/Entity/Reports/Templates/ReportTemplatesTable.vue"),
				beforeEnter: checkProductionSubscription,
				meta: {
					pageName: "Production Settings Report Templates",
					//permission: RoutePermission.Manage,
					permissionId: "productionId",
					friendlyPath: "/productions/**/settings/report-templates",
					boxed: true,
					fullWidth: true,
				},
			},
			{
				path: "report-templates/:reportTemplateId",
				name: "production-settings-report-template-editor",
				props: true,
				component: () => import("@/components/Entity/Reports/Templates/ReportTemplateEditor.vue"),
				beforeEnter: checkProductionSubscription,
				meta: {
					pageName: "Production Settings Report Template Editor",
					//permission: RoutePermission.Manage,
					permissionId: "productionId",
					friendlyPath: "/productions/**/settings/report-templates/**",
					boxed: true,
					fullWidth: true,
				},
			},
		],
	},
	{
		path: "/productions/:productionId/people",
		name: "production-people",
		props: true,
		beforeEnter: checkProductionSubscription,
		component: () => import("@/components/Entity/People/PeopleMembersView.vue"),
		meta: {
			pageName: "Production People",
			//permission: RoutePermission.Access,
			permissionId: "productionId",
			friendlyPath: "/productions/**/people",
			fullWidth: true,
			showDetailsHeader: true,
			header: "Production Members",
			showBackButton: false,
			actionButton: "Invite People",
			actionButtonEmits: "modal:people:add",
			actionButtonManagerOnly: true,
		},
	},
	{
		path: "/productions/:productionId/people/groups",
		name: "production-people-groups",
		props: true,
		beforeEnter: checkProductionSubscription,
		component: () => import("@/components/Entity/People/GroupsView.vue"),
		meta: {
			pageName: "Production People Groups",
			permissionId: "productionId",
			friendlyPath: "/productions/**/people/groups",
			fullWidth: true,
			showDetailsHeader: true,
			header: "Production Groups",
			showBackButton: false,
		},
	},
	{
		path: "/productions/:productionId/people/requests",
		name: "production-people-requests",
		props: true,
		beforeEnter: checkProductionSubscription,
		component: () => import("@/components/Entity/People/PeopleRequestsView.vue"),
		meta: {
			pageName: "Production People Requests",
			//permission: RoutePermission.Access,
			permissionId: "productionId",
			friendlyPath: "/productions/**/people/requests",
			fullWidth: true,
			showDetailsHeader: true,
			header: "Membership Requests",
			showBackButton: false,
			actionButton: "Invite People",
			actionButtonEmits: "modal:people:add",
			actionButtonManagerOnly: true,
		},
	},
];
